import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Card, Skeleton, Result, Alert } from "antd";
import {
  getGroupMailPropertiesForGroup,
  patchUpdateGroupMailProperties,
  getScopesForGroup,
} from "../../../actions/api";
import MailAliases from "./MailAliases";
import MailPostingRestrictions from "./MailPostingRestrictions";
import MailDeliveryNotification from "./MailDeliveryNotification";
import MailArchive from "./MailArchive";
import EGroupWarning from "../EGroupWarning.js";
import { errorAction, types } from "@authzsvc/api-lib";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../util.js";

const GroupEmailProperties = ({
  selectedGroup,
  canManageGroup,
  isReplicaGroup,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [groupMailProperties, setGroupMailProperties] = useState({});
  const [selectedGroupScopes, setSelectedGroupScopes] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isScopesInitialized, setIsScopesInitialized] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const canModifyGroup = canManageGroup && !isReplicaGroup;
  const groupCreationDateValue = new Date(selectedGroup.creationTime);
  const groupAgeInMilliseconds = Date.now() - groupCreationDateValue.getTime();
  const groupSyncTypeIsPrimary = selectedGroup?.syncType === "Master";
  const isGroupLessThanOneHourOld =
    typeof groupAgeInMilliseconds === "number" &&
    groupAgeInMilliseconds >= 0 &&
    groupAgeInMilliseconds < 60 * 60 * 1000;
  const requiredMailSyncScopes = ["ActiveDirectory", "EmailDistributionList"];
  const selectedScopeNames = selectedGroupScopes.map(
    (scope) => scope.displayName
  );
  const hasAllRequiredMailSyncScopes = requiredMailSyncScopes.every((name) =>
    selectedScopeNames.includes(name)
  );
  const shouldShowMissingScopeWarning =
    isScopesInitialized && !hasAllRequiredMailSyncScopes;
  const {
    id,
    primaryMailAlias,
    mailAliases = [],
    allowAuthenticatedMessagesOnly = false,
    openPosting = false,
    identitiesAllowedToPost = [],
    groupsAllowedToPost = [],
    deliveryFailureReport,
    enableArchive = false,
  } = groupMailProperties || {};

  const fetchGroupMailProperties = useCallback(() => {
    setLoading(true);

    dispatch(getGroupMailPropertiesForGroup(selectedGroup.id))
      .then((response) => {
        if (
          response?.type ===
          errorAction(types.GET_GROUP_MAIL_PROPERTIES_FOR_GROUP)
        ) {
          if (response?.payload?.data?.status === 403) {
            setBlocked(true);
          }
        } else {
          setGroupMailProperties(response.payload.data);
        }
      })
      .catch(() => {
        showErrorNotification("Failed to fetch group mail properties.", "");
      })
      .finally(() => {
        setIsInitialized(true);
        setLoading(false);
      });
  }, [dispatch, selectedGroup.id]);

  const fetchGroupScopes = useCallback(() => {
    dispatch(getScopesForGroup(selectedGroup.id))
      .then((response) => {
        if (response?.type === errorAction(types.GET_SCOPES_FOR_GROUP)) {
          setSelectedGroupScopes([]);
        } else {
          setSelectedGroupScopes(response?.payload?.data || []);
        }
      })
      .catch(() => {
        setSelectedGroupScopes([]);
      })
      .finally(() => {
        setIsScopesInitialized(true);
      });
  }, [dispatch, selectedGroup.id]);

  const toJsonPatch = (updates) => {
    return Object.entries(updates).map(([key, value]) => ({
      op: "replace",
      path: `/${key}`,
      value: value,
    }));
  };

  const updateGroupMailProperties = async (updates) => {
    const patchBody = toJsonPatch(updates);

    setLoading(true);

    dispatch(patchUpdateGroupMailProperties(id, patchBody))
      .then((actionResult) => {
        if (
          actionResult?.type === errorAction(types.PATCH_GROUP_MAIL_PROPERTIES)
        ) {
          return Promise.reject(
            new Error("Failed to update group mail properties.")
          );
        }
        fetchGroupMailProperties();
      })
      .then(() => {
        showSuccessNotification(
          "Group Email Properties updated successfully!",
          ""
        );
      })
      .catch(() => {
        showErrorNotification("Failed to update group mail properties.", "");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchGroupMailProperties();
  }, [fetchGroupMailProperties]);

  useEffect(() => {
    fetchGroupScopes();
  }, [fetchGroupScopes]);

  return (
    <div style={{ position: "relative" }}>
      {isInitialized ? (
        <>
          {shouldShowMissingScopeWarning || !groupSyncTypeIsPrimary ? (
            <Alert
              type="warning"
              showIcon
              message="For this group to synchronise its settings to the mailing list system please ensure that group scopes are enabled in the details tab, and that the synchronisation type is set to Primary."
              style={{ marginBottom: 16 }}
            />
          ) : (
            isGroupLessThanOneHourOld && (
              <Alert
                type="warning"
                showIcon
                message="Please wait at least one hour following group creation before sending a mail, in order for the email settings to be fully synchronised."
                style={{ marginBottom: 16 }}
              />
            )
          )}

          {blocked ? (
            <Result
              status="403"
              title="You don't have the access rights to view email properties."
            />
          ) : (
            <>
              <EGroupWarning
                selectedGroup={selectedGroup}
                canManageGroup={canManageGroup}
                showSyncMessage={false}
              />
              <MailAliases
                primaryMailAlias={primaryMailAlias}
                mailAliases={mailAliases}
                canModifyGroup={canModifyGroup && !loading}
                updateGroupMailProperties={updateGroupMailProperties}
              />
              <MailPostingRestrictions
                groupMailPropertiesId={id}
                identitiesAllowedToPost={identitiesAllowedToPost}
                groupsAllowedToPost={groupsAllowedToPost}
                selectedGroup={selectedGroup}
                allowAuthenticatedMessagesOnly={allowAuthenticatedMessagesOnly}
                openPosting={openPosting}
                canModifyGroup={canModifyGroup && !loading}
                updateGroupMailProperties={updateGroupMailProperties}
                fetchGroupMailProperties={fetchGroupMailProperties}
                setLoading={setLoading}
              />
              <MailDeliveryNotification
                deliveryFailureReport={deliveryFailureReport}
                canModifyGroup={canModifyGroup && !loading}
                updateGroupMailProperties={updateGroupMailProperties}
              />
              <MailArchive
                enableArchive={enableArchive}
                selectedGroup={selectedGroup}
                canModifyGroup={canModifyGroup && !loading}
                updateGroupMailProperties={updateGroupMailProperties}
              />{" "}
            </>
          )}
        </>
      ) : (
        <div>
          {[1, 2, 3, 4].map((key) => (
            <Card key={key} style={{ marginBottom: 14 }}>
              <Skeleton
                active
                title={{ width: "40%" }}
                paragraph={{ rows: 3, width: ["80%", "90%", "60%"] }}
              />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

GroupEmailProperties.propTypes = {
  selectedGroup: PropTypes.object,
  canManageGroup: PropTypes.bool,
};

export default GroupEmailProperties;
